import React, { useState, useCallback, useEffect } from 'react';
import { View, Text, TouchableOpacity, ScrollView, Alert, ActivityIndicator, Platform } from 'react-native';
import { XStack, YStack, Card, Button } from 'tamagui';
import { MaterialIcons, Feather } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system';
import { useDispatch } from 'react-redux';
import { SHOW_LOADING_MODAL, HIDE_LOADING_MODAL, SHOW_AUTH_MODAL } from '@reduxLocal/actions/types';
import apis from '@api/api';
import * as Linking from 'expo-linking';

interface FileItem {
  id: string;
  name: string;
  type: string;
  size?: number;
  uploadedAt: string | Date;
  uploadedBy?: string;
  url?: string;
}

interface ModernProjectFilesProps {
  projectId: string;
  files?: FileItem[];
  onUpload?: (files: FileList) => void;
  onDelete?: (fileId: string) => void;
  onDownload?: (file: FileItem) => void;
}

const ModernProjectFiles: React.FC<ModernProjectFilesProps> = ({
  projectId,
  files: propFiles,
  onUpload: propOnUpload,
  onDelete: propOnDelete,
  onDownload: propOnDownload
}) => {
  const theme = themeSelector();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

  // Fetch project files from API
  const { data: filesData, isLoading, refetch } = useQuery({
    queryKey: ['projectFiles', projectId],
    queryFn: async () => {
      try {
        const response = await apis.getProjectFiles(projectId);
        // Handle both response formats: { success: true, data: [...] } or direct array
        const files = response?.data || response || [];
        console.log('Fetched project files:', files);
        return Array.isArray(files) ? files : [];
      } catch (error) {
        console.error('Error fetching project files:', error);
        return [];
      }
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Use API data if available, otherwise use prop files
  const files = filesData || propFiles || [];

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (fileData: { name: string; type: string; size?: number; fileData: string }) => {
      return await apis.uploadProjectFiles(projectId, fileData);
    },
    onSuccess: () => {
      // Invalidate and refetch the query to refresh the file list
      queryClient.invalidateQueries({ queryKey: ['projectFiles', projectId] });
      queryClient.refetchQueries({ queryKey: ['projectFiles', projectId] });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'File uploaded successfully', type: 'success' }
      });
    },
    onError: (error) => {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Failed to upload file', type: 'error' }
      });
      console.error('Error uploading file:', error);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      return await apis.deleteProjectFile(projectId, documentId);
    },
    onSuccess: () => {
      // Invalidate and refetch the query to refresh the file list
      queryClient.invalidateQueries({ queryKey: ['projectFiles', projectId] });
      queryClient.refetchQueries({ queryKey: ['projectFiles', projectId] });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'File deleted successfully', type: 'success' }
      });
    },
    onError: (error) => {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: 'Failed to delete file', type: 'error' }
      });
      console.error('Error deleting file:', error);
    },
  });

  const formatFileSize = (bytes?: number) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date: Date | string) => {
    const dateObj = date instanceof Date ? date : new Date(date);
    const now = new Date();
    const diffInMs = now.getTime() - dateObj.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      if (diffInHours === 0) {
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes}m ago`;
      }
      return `${diffInHours}h ago`;
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else {
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: dateObj.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const getFileIcon = (fileName: string, fileType?: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    const lowerType = fileType?.toLowerCase() || '';

    if (lowerType.includes('pdf') || extension === 'pdf') return 'picture-as-pdf';
    if (lowerType.includes('word') || lowerType.includes('msword') || extension === 'doc' || extension === 'docx') return 'description';
    if (lowerType.includes('excel') || lowerType.includes('spreadsheet') || extension === 'xls' || extension === 'xlsx' || extension === 'csv') return 'table-chart';
    if (lowerType.includes('powerpoint') || lowerType.includes('presentation') || extension === 'ppt' || extension === 'pptx') return 'slideshow';
    if (lowerType.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || '')) return 'image';
    if (lowerType.includes('video') || ['mp4', 'avi', 'mov', 'wmv'].includes(extension || '')) return 'movie';
    if (['zip', 'rar', '7z'].includes(extension || '')) return 'archive';
    if (lowerType.includes('text') || extension === 'txt') return 'article';
    return 'insert-drive-file';
  };

  const toggleFileSelection = (fileId: string) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleDeleteSelected = () => {
    Alert.alert(
      'Delete Files',
      `Are you sure you want to delete ${selectedFiles.length} file(s)?`,
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Delete', 
          style: 'destructive',
          onPress: () => {
            selectedFiles.forEach(fileId => {
              if (propOnDelete) {
                propOnDelete(fileId);
              } else {
                deleteMutation.mutate(fileId);
              }
            });
            setSelectedFiles([]);
          }
        }
      ]
    );
  };

  const handleFileUpload = useCallback(async () => {
    dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Selecting document...' } });

    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'text/plain',
          'text/rtf',
          'text/csv',
          'image/*',
          'application/rtf',
          'application/vnd.oasis.opendocument.text',
          'application/vnd.oasis.opendocument.spreadsheet',
          'application/vnd.oasis.opendocument.presentation',
          '*/*'
        ],
        copyToCacheDirectory: true,
        multiple: false,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        dispatch({ type: HIDE_LOADING_MODAL });
        return;
      }

      const file = result.assets[0];
      dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Reading file...' } });

      // Read file as base64
      let base64Data: string;
      try {
        if (file.uri.startsWith('data:')) {
          base64Data = file.uri;
        } else if (file.uri.includes('base64,')) {
          base64Data = file.uri;
        } else {
          if (Platform.OS === 'web') {
            const response = await fetch(file.uri);
            const blob = await response.blob();
            base64Data = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          } else {
            try {
              const fileInfo = await FileSystem.getInfoAsync(file.uri);
              if (!fileInfo.exists) {
                throw new Error('File does not exist at URI');
              }

              base64Data = await FileSystem.readAsStringAsync(file.uri, {
                encoding: 'base64' as any,
              });

              const mimeType = file.mimeType || 'application/octet-stream';
              if (!base64Data.startsWith('data:')) {
                base64Data = `data:${mimeType};base64,${base64Data}`;
              }
            } catch (fileSystemError) {
              console.log('FileSystem read failed, trying fetch:', fileSystemError);
              const response = await fetch(file.uri);
              const blob = await response.blob();
              base64Data = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            }
          }
        }

        if (!base64Data || base64Data.length === 0) {
          throw new Error('Failed to read file data');
        }
      } catch (readError) {
        dispatch({ type: HIDE_LOADING_MODAL });
        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            content: `Failed to read file: ${readError instanceof Error ? readError.message : 'Unknown error'}`,
            type: 'error'
          }
        });
        console.error('Error reading file:', readError);
        return;
      }

      // Determine MIME type
      let mimeType = file.mimeType;
      if (!mimeType || mimeType === 'application/octet-stream') {
        const extension = file.name.split('.').pop()?.toLowerCase();
        const mimeTypeMap: Record<string, string> = {
          'pdf': 'application/pdf',
          'doc': 'application/msword',
          'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'xls': 'application/vnd.ms-excel',
          'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'ppt': 'application/vnd.ms-powerpoint',
          'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'txt': 'text/plain',
          'rtf': 'application/rtf',
          'csv': 'text/csv',
        };
        mimeType = extension ? (mimeTypeMap[extension] || 'application/octet-stream') : 'application/octet-stream';
      }

      dispatch({ type: SHOW_LOADING_MODAL, data: { content: 'Uploading file...' } });

      // Upload file using mutation (which will automatically refresh the query)
      await uploadMutation.mutateAsync({
        name: file.name,
        type: mimeType,
        size: file.size,
        fileData: base64Data,
      });

      dispatch({ type: HIDE_LOADING_MODAL });
    } catch (error) {
      dispatch({ type: HIDE_LOADING_MODAL });
      // Error handling is done in mutation onError
      console.error('Error uploading file:', error);
    }
  }, [projectId, dispatch, uploadMutation]);

  const handleDelete = useCallback((fileId: string) => {
    Alert.alert(
      'Delete File',
      'Are you sure you want to delete this file?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            if (propOnDelete) {
              propOnDelete(fileId);
            } else {
              deleteMutation.mutate(fileId);
            }
          }
        }
      ]
    );
  }, [propOnDelete, deleteMutation]);

  const handleDownload = useCallback(async (file: FileItem) => {
    if (propOnDownload) {
      propOnDownload(file);
      return;
    }

    if (file.url) {
      try {
        const canOpen = await Linking.canOpenURL(file.url);
        if (canOpen) {
          await Linking.openURL(file.url);
        } else {
          Alert.alert('Error', 'Cannot open file URL');
        }
      } catch (error) {
        console.error('Error opening file:', error);
        Alert.alert('Error', 'Failed to open file');
      }
    } else {
      Alert.alert('Error', 'File URL not available');
    }
  }, [propOnDownload]);

  const renderFileItem = (file: FileItem) => (
    <Card
      key={file.id}
      padding="$3"
      backgroundColor={theme ? '#ffffff' : '#1f2937'}
      borderRadius="$2"
      marginBottom="$2"
      borderWidth={selectedFiles.includes(file.id) ? 2 : 1}
      borderColor={selectedFiles.includes(file.id) ? '#3b82f6' : (theme ? '#e5e7eb' : '#374151')}
    >
      <XStack alignItems="center" gap="$3">
        <TouchableOpacity
          onPress={() => toggleFileSelection(file.id)}
          style={{
            width: 20,
            height: 20,
            borderRadius: 3,
            borderWidth: 2,
            borderColor: selectedFiles.includes(file.id) ? '#3b82f6' : '#9ca3af',
            backgroundColor: selectedFiles.includes(file.id) ? '#3b82f6' : 'transparent',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {selectedFiles.includes(file.id) && (
            <MaterialIcons name="check" size={12} color="#ffffff" />
          )}
        </TouchableOpacity>

        <MaterialIcons 
          name={getFileIcon(file.name, file.type) as any} 
          size={24} 
          color={theme ? '#6b7280' : '#9ca3af'} 
        />

        <YStack flex={1}>
          <Text style={{
            fontSize: 14,
            fontWeight: '500',
            color: theme ? '#111827' : '#ffffff',
          }}>
            {file.name}
          </Text>
          <Text style={{
            fontSize: 12,
            color: theme ? '#6b7280' : '#9ca3af',
          }}>
            {file.size && formatFileSize(file.size)} • {formatDate(file.uploadedAt)} {file.uploadedBy && `• ${file.uploadedBy}`}
          </Text>
        </YStack>

        <XStack gap="$2">
          <TouchableOpacity
            onPress={() => handleDownload(file)}
            style={{
              padding: 8,
              borderRadius: 6,
              backgroundColor: theme ? '#f3f4f6' : '#374151',
            }}
          >
            <MaterialIcons 
              name="download" 
              size={16} 
              color={theme ? '#6b7280' : '#9ca3af'} 
            />
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => handleDelete(file.id)}
            style={{
              padding: 8,
              borderRadius: 6,
              backgroundColor: theme ? '#f3f4f6' : '#374151',
            }}
          >
            <MaterialIcons 
              name="delete" 
              size={16} 
              color="#ef4444" 
            />
          </TouchableOpacity>
        </XStack>
      </XStack>
    </Card>
  );

  if (isLoading) {
    return (
      <View style={{
        flex: 1,
        backgroundColor: theme ? '#f9fafb' : '#111827',
        padding: 20,
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <ActivityIndicator size="large" color={theme ? '#3b82f6' : '#60a5fa'} />
        <Text style={{
          marginTop: 16,
          color: theme ? '#6b7280' : '#9ca3af',
        }}>
          Loading files...
        </Text>
      </View>
    );
  }

  return (
    <View style={{ 
      flex: 1, 
      backgroundColor: theme ? '#f9fafb' : '#111827',
      padding: 20 
    }}>
      {/* Header */}
      <XStack justifyContent="space-between" alignItems="center" marginBottom="$4">
        <Text style={{
          fontSize: 20,
          fontWeight: '700',
          color: theme ? '#111827' : '#ffffff',
        }}>
          Project Files
        </Text>

        <XStack gap="$2">
          <TouchableOpacity
            onPress={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}
            style={{
              padding: 8,
              borderRadius: 6,
              backgroundColor: theme ? '#f3f4f6' : '#374151',
            }}
          >
            <MaterialIcons 
              name={viewMode === 'list' ? 'grid-view' : 'view-list'} 
              size={20} 
              color={theme ? '#6b7280' : '#9ca3af'} 
            />
          </TouchableOpacity>

          <Button
            onPress={handleFileUpload}
            backgroundColor="#3b82f6"
            color="#ffffff"
            size="$3"
            disabled={uploadMutation.isPending}
          >
            {uploadMutation.isPending ? (
              <ActivityIndicator size="small" color="#ffffff" />
            ) : (
              <>
                <Feather name="upload" size={16} color="#ffffff" style={{ marginRight: 4 }} />
                  Upload Files
              </>
            )}
          </Button>
        </XStack>
      </XStack>

      {/* Bulk Actions */}
      {selectedFiles.length > 0 && (
        <Card
          padding="$3"
          backgroundColor={theme ? '#ffffff' : '#1f2937'}
          borderRadius="$2"
          marginBottom="$4"
        >
          <XStack justifyContent="space-between" alignItems="center">
            <Text style={{
              fontSize: 14,
              fontWeight: '500',
              color: theme ? '#111827' : '#ffffff',
            }}>
              {selectedFiles.length} file(s) selected
            </Text>

            <XStack gap="$2">
              <Button
                variant="outlined"
                size="$2"
                borderColor={theme ? '#e5e7eb' : '#4b5563'}
                backgroundColor="transparent"
              >
                Download
              </Button>
              <Button
                onPress={handleDeleteSelected}
                backgroundColor="#ef4444"
                color="#ffffff"
                size="$2"
              >
                Delete
              </Button>
            </XStack>
          </XStack>
        </Card>
      )}

      {/* File Stats */}
      <XStack gap="$4" marginBottom="$4">
        <Card
          flex={1}
          padding="$3"
          backgroundColor={theme ? '#ffffff' : '#1f2937'}
          borderRadius="$2"
        >
          <Text style={{
            fontSize: 12,
            color: theme ? '#6b7280' : '#9ca3af',
            marginBottom: 4,
          }}>
            Total Files
          </Text>
          <Text style={{
            fontSize: 18,
            fontWeight: '600',
            color: theme ? '#111827' : '#ffffff',
          }}>
            {files.length}
          </Text>
        </Card>

        <Card
          flex={1}
          padding="$3"
          backgroundColor={theme ? '#ffffff' : '#1f2937'}
          borderRadius="$2"
        >
          <Text style={{
            fontSize: 12,
            color: theme ? '#6b7280' : '#9ca3af',
            marginBottom: 4,
          }}>
            Storage Used
          </Text>
          <Text style={{
            fontSize: 18,
            fontWeight: '600',
            color: theme ? '#111827' : '#ffffff',
          }}>
            {formatFileSize(files.reduce((total, file) => total + (file.size || 0), 0))}
          </Text>
        </Card>
      </XStack>

      {/* Files List */}
      <ScrollView style={{ flex: 1 }}>
        {files.length === 0 ? (
          <Card
            padding="$6"
            backgroundColor={theme ? '#ffffff' : '#1f2937'}
            borderRadius="$3"
            alignItems="center"
          >
            <MaterialIcons 
              name="folder-open" 
              size={48} 
              color={theme ? '#d1d5db' : '#4b5563'} 
              style={{ marginBottom: 16 }}
            />
            <Text style={{
              fontSize: 16,
              fontWeight: '500',
              color: theme ? '#6b7280' : '#9ca3af',
              textAlign: 'center',
              marginBottom: 8,
            }}>
              No files uploaded yet
            </Text>
            <Text style={{
              fontSize: 14,
              color: theme ? '#9ca3af' : '#6b7280',
              textAlign: 'center',
              marginBottom: 16,
            }}>
              Upload files to share with your team
            </Text>
            <Button
              onPress={handleFileUpload}
              backgroundColor="#3b82f6"
              color="#ffffff"
              disabled={uploadMutation.isPending}
            >
              {uploadMutation.isPending ? (
                <ActivityIndicator size="small" color="#ffffff" />
              ) : (
                'Upload First File'
              )}
            </Button>
          </Card>
        ) : (
          <YStack>
            {files.map(renderFileItem)}
          </YStack>
        )}
      </ScrollView>
    </View>
  );
};

export default ModernProjectFiles;
